import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { ClientSuggestion, CreateClientDraft } from "../types";
import styles from "./ClientContactField.module.css";

export interface ClientFormProps {
  /** Persists the draft (create or update); returns the saved client. */
  saveClient: (draft: CreateClientDraft) => Promise<ClientSuggestion>;
  /** Pre-fills the fields for edit mode; omit for a blank create form. */
  initial?: CreateClientDraft;
  heading?: string;
  submitLabel?: string;
  /** Called with the saved client on success. */
  onSaved: (client: ClientSuggestion) => void;
  onCancel: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CLIENT_TYPE_OPTIONS = ["company", "sole trader", "partnership", "trust", "person"] as const;

/**
 * Inline client form shared by the New Job combobox (create), the /clients
 * "New client" panel (create), and the /clients edit modal (edit) — one set of
 * fields, validation, and error reporting everywhere a client is captured.
 *
 * Rendered as a `<div>` (not a `<form>`) because in the New Job form it is
 * nested inside the parent request form; Enter submits via the key handler.
 */
export function ClientForm({
  saveClient,
  initial,
  heading = "Create a new client",
  submitLabel = "Create Client",
  onSaved,
  onCancel,
}: ClientFormProps) {
  const [legalName, setLegalName] = useState(initial?.legalName ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [address, setAddress] = useState(initial?.address ?? "");
  const [notes, setNotes] = useState(initial?.notes ?? "");
  const [abnNumber, setAbnNumber] = useState(initial?.abnNumber ?? "");
  const [acnNumber, setAcnNumber] = useState(initial?.acnNumber ?? "");
  const [type, setType] = useState<string>(initial?.type ?? "company");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const submit = async () => {
    // A second activation while the request is in flight would mint a duplicate
    // client, so the guard is here and not only on the button's disabled state:
    // Enter reaches this directly through the key handler.
    if (submitting) return;
    const trimmed = legalName.trim();
    if (!trimmed) {
      setError("Client name is required.");
      nameRef.current?.focus();
      return;
    }
    if (email.trim() && !EMAIL_PATTERN.test(email.trim())) {
      setError("Enter a valid email, or leave it blank.");
      return;
    }
    setSubmitting(true);
    try {
      const client = await saveClient({
        legalName: trimmed,
        phone,
        email,
        address,
        notes,
        abnNumber,
        acnNumber,
        type,
      });
      onSaved(client);
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Client save failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !(event.target instanceof HTMLTextAreaElement)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className={styles.create} role="group" aria-label={heading} onKeyDown={onKeyDown}>
      <span className={styles.createHeading}>{heading}</span>
      <label className={styles.createField}>
        <span className={styles.createLabel}>Client name *</span>
        <input
          ref={nameRef}
          className={styles.input}
          name="clientLegalName"
          value={legalName}
          autoComplete="off"
          placeholder="e.g. Morningside Apartments"
          aria-required="true"
          onChange={(event) => {
            setLegalName(event.currentTarget.value);
            if (error) setError("");
          }}
        />
      </label>
      <div className={styles.pair}>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Phone</span>
          <input
            className={styles.input}
            name="clientPhone"
            value={phone}
            autoComplete="off"
            onChange={(event) => setPhone(event.currentTarget.value)}
          />
        </label>
        <label className={styles.createField}>
          <span className={styles.createLabel}>Email</span>
          <input
            className={styles.input}
            name="clientEmail"
            type="email"
            value={email}
            autoComplete="off"
            onChange={(event) => {
              setEmail(event.currentTarget.value);
              if (error) setError("");
            }}
          />
        </label>
      </div>
      <label className={styles.createField}>
        <span className={styles.createLabel}>Address</span>
        <input
          className={styles.input}
          name="clientAddress"
          value={address}
          autoComplete="off"
          onChange={(event) => setAddress(event.currentTarget.value)}
        />
      </label>
      <div className={styles.pair}>
        <label className={styles.createField}>
          <span className={styles.createLabel}>ABN</span>
          <input
            className={styles.input}
            name="clientAbn"
            value={abnNumber}
            autoComplete="off"
            placeholder="e.g. 51 824 753 556"
            onChange={(event) => setAbnNumber(event.currentTarget.value)}
          />
        </label>
        <label className={styles.createField}>
          <span className={styles.createLabel}>ACN</span>
          <input
            className={styles.input}
            name="clientAcn"
            value={acnNumber}
            autoComplete="off"
            placeholder="e.g. 002 249 518"
            onChange={(event) => setAcnNumber(event.currentTarget.value)}
          />
        </label>
      </div>
      <label className={styles.createField}>
        <span className={styles.createLabel}>Type</span>
        <select
          className={styles.input}
          name="clientType"
          value={type}
          onChange={(event) => setType(event.currentTarget.value)}
        >
          {CLIENT_TYPE_OPTIONS.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>
      <label className={styles.createField}>
        <span className={styles.createLabel}>Notes</span>
        <textarea
          className={styles.textarea}
          name="clientNotes"
          rows={2}
          value={notes}
          onChange={(event) => setNotes(event.currentTarget.value)}
        />
      </label>
      {error ? (
        <span role="alert" className={styles.createError}>
          {error}
        </span>
      ) : null}
      <div className={styles.actions}>
        <button type="button" className={styles.cancel} onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.submit}
          onClick={() => void submit()}
          disabled={submitting}
        >
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </div>
  );
}
